/**
 * Kabuk — **vücut** mertebesi: sayfa iskeleti.
 *
 * Yapı:
 *   ORTAM ŞERİDİ (canlıysa kırmızı, gizlenemez)
 *   ├─ geniş ekran: sabit kenar çubuğu + içerik
 *   └─ telefon:     üst çubuk + kayan menü (drawer)
 *
 * Ortam şeridi neden en üstte ve neden gizlenemez: canlı ortamda kesilen fatura
 * GERÇEKTİR ve GİB'e raporlanır; geri alınamaz. Kullanıcı hangi ortamda
 * olduğunu bilmeden hiçbir işlem yapmamalı.
 *
 * Mobil kararlar:
 *   - Menü `position: fixed` bir katman olarak açılır; arka plan kararır ve
 *     tıklanınca kapanır. Esc de kapatır.
 *   - Menü açıkken gövde kaydırması kilitlenir (arka plan kaymasın).
 *   - Sayfa değişince menü kendiliğinden kapanır — kullanıcı iki kez
 *     dokunmak zorunda kalmasın.
 *   - Dokunma hedefleri 44px (styles.css `pointer: coarse` kuralı).
 */

import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router';
import {
  FileText,
  LayoutDashboard,
  LogOut,
  Menu,
  Settings,
  ShieldAlert,
  ShieldCheck,
  TriangleAlert,
  Users,
  X,
  AlertTriangle,
  ListChecks,
  LayoutTemplate,
  Send,
  ClipboardCheck,
} from 'lucide-react';
import { useOturum } from '../oturum';
import { izinVar } from '../lib/yetki';
import { Dugme } from './ui/Dugme';
import { cn } from '../lib/cn';
import { onayIste } from '../lib/bildirim';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { kilitteAcikMi } from '../lib/kurulum';
import { SayfaBasligi, type KirintiOgesi } from './SayfaBasligi';
import type { Kullanici } from '../lib/api';

interface MenuOgesi {
  yol: string;
  etiket: string;
  ikon: typeof LayoutDashboard;
  /** Bu izin yoksa menüde HİÇ gösterilmez. */
  gerekenIzin?: string;
}

interface MenuBolumu {
  /** Bölüm başlığı. Tek öğeli bölümde gösterilmez — gereksiz gürültü olur. */
  baslik: string;
  ogeler: MenuOgesi[];
}

/**
 * Menü BÖLÜMLERE ayrılır (Vuexy deseni).
 *
 * Neden: düz bir liste altı öğeden sonra taranamaz hâle geliyor. "Günlük iş"
 * ile "yönetim" ayrı zihinsel kutular; menüde de ayrı durmalı. Kullanıcı
 * "ayarlar nerede" diye tek tek okumak yerine doğrudan YÖNETİM başlığına bakar.
 */
const MENU_BOLUMLERI: MenuBolumu[] = [
  {
    baslik: 'Günlük iş',
    ogeler: [
      { yol: '/', etiket: 'Genel bakış', ikon: LayoutDashboard, gerekenIzin: 'fatura.goruntule' },
      { yol: '/faturalar', etiket: 'Faturalar', ikon: FileText, gerekenIzin: 'fatura.goruntule' },
      { yol: '/onay', etiket: 'Onay bekleyen', ikon: ClipboardCheck, gerekenIzin: 'fatura.goruntule' },
    ],
  },
  {
    baslik: 'Yönetim',
    ogeler: [
      // Kurulum en üstte: ilk günlerde en çok açılacak yer, sonra kontrol listesi.
      { yol: '/kurulum', etiket: 'Kurulum', ikon: ListChecks, gerekenIzin: 'ayar.goruntule' },
      { yol: '/kullanicilar', etiket: 'Kullanıcılar', ikon: Users, gerekenIzin: 'kullanici.goruntule' },
      { yol: '/roller', etiket: 'Roller ve yetkiler', ikon: ShieldCheck, gerekenIzin: 'rol.goruntule' },
      { yol: '/sablonlar', etiket: 'Şablonlar', ikon: LayoutTemplate, gerekenIzin: 'sablon.goruntule' },
      { yol: '/ayarlar', etiket: 'Ayarlar', ikon: Settings, gerekenIzin: 'ayar.goruntule' },
    ],
  },
  {
    baslik: 'Denetim',
    ogeler: [
      // Hata kayıtları önce: bir şey ters gittiğinde ilk bakılacak yer burası.
      { yol: '/hatalar', etiket: 'Hata kayıtları', ikon: AlertTriangle, gerekenIzin: 'hata.goruntule' },
      { yol: '/gonderimler', etiket: 'Gönderim kayıtları', ikon: Send, gerekenIzin: 'bildirim.goruntule' },
      { yol: '/denetim', etiket: 'İşlem kaydı', ikon: ShieldAlert, gerekenIzin: 'kayit.goruntule' },
    ],
  },
];

function OrtamSeridi() {
  const { canli, ortam } = useOturum();

  if (canli) {
    return (
      <div
        role="alert"
        className="flex items-center justify-center gap-2 bg-[var(--live-warning)] px-4 py-2 text-center text-xs font-semibold text-[var(--live-warning-ink)]"
      >
        <TriangleAlert size={14} strokeWidth={2.5} className="shrink-0" aria-hidden="true" />
        <span>CANLI ORTAM — kesilen faturalar gerçektir ve GİB’e raporlanır</span>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center justify-center gap-x-2 border-b border-[var(--line)] bg-[var(--surface-sunken)] px-4 py-1.5 text-center text-xs font-medium text-[var(--ink-muted)]">
      <span>TEST ORTAMI — kesilen faturalar gerçek değildir</span>
      <span className="rakam text-[var(--ink-faint)]">({ortam})</span>
    </div>
  );
}

function Gezinme({ kullanici, kapat }: { kullanici: Kullanici | null; kapat?: () => void }) {
  /*
   * Kurulum tamamlanmadıysa menü Kurulum + Ayarlar'a daralır: kilitli yollara
   * bağlantı göstermek, tıklayıp geri atılmak demektir. Sorgu paylaşımlı
   * (App.tsx aynı anahtarı kullanır) — ikinci istek atılmaz.
   */
  const { data: kilit } = useQuery({
    queryKey: ['kurulum-ozet'],
    queryFn: api.kurulumOzeti,
    staleTime: 30_000,
  });
  // Süper yönetici muaf (App.tsx KurulumKapisi ile aynı kural) — menüsü daralmaz.
  const kilitli = kilit !== undefined && !kilit.faturaKesilebilir && !kullanici?.isSuperAdmin;

  /*
   * Boş bölüm HİÇ çizilmez. İzleyici rolünde "Yönetim" başlığı altında hiçbir
   * öğe kalmıyor; başlığı tek başına göstermek "bir şey eksik" hissi verirdi.
   */
  const gorunurBolumler = MENU_BOLUMLERI.map((bolum) => ({
    ...bolum,
    ogeler: bolum.ogeler.filter(
      (oge) =>
        (!oge.gerekenIzin || izinVar(kullanici, oge.gerekenIzin)) && (!kilitli || kilitteAcikMi(oge.yol))
    ),
  })).filter((bolum) => bolum.ogeler.length > 0);

  return (
    <nav className="flex flex-col gap-5" aria-label="Ana gezinme">
      {gorunurBolumler.map((bolum) => (
        <div key={bolum.baslik} className="flex flex-col gap-1">
          <span className="ust-etiket px-3 pb-1">{bolum.baslik}</span>
          {bolum.ogeler.map(({ yol, etiket, ikon: Ikon }) => (
            <NavLink
              key={yol}
              to={yol}
              end={yol === '/'}
              onClick={kapat}
              className={({ isActive }) =>
                cn(
                  /*
                   * Ölçüldü (Vuexy 10.11 _menu.scss): seçili öğe DOLGULU vurgu
                   * rengi + hafif gölge + beyaz metin. Soluk tint ile
                   * karşılaştırıldı: dolgulu hap menüde "neredeyim" sorusunu
                   * göz kaydırmadan cevaplıyor; tint, koyu temada kayboluyordu.
                   * Dolgu 8px/12px = $menu-vertical-link-padding.
                   */
                  'group relative flex items-center gap-3 rounded-[var(--radius-md)] px-3 py-2',
                  'text-sm font-medium transition-colors duration-150',
                  isActive
                    ? 'bg-[var(--accent)] text-[var(--accent-ink)] shadow-[var(--shadow-vurgu)]'
                    : 'text-[var(--ink-muted)] hover:bg-[var(--surface-hover)] hover:text-[var(--ink)]'
                )
              }
            >
              {() => (
                <>
                  {/*
                    Sol çubuk kaldırıldı: dolgulu hap zaten konumu iki yoldan
                    (zemin + metin rengi) söylüyor; üçüncü işaret gürültüydü.
                  */}
                  {/* İkon 1px yukarı — optik hizalama (docs/13 bölüm 5). */}
                  <span className="-mt-px shrink-0" aria-hidden="true">
                    <Ikon size={17} strokeWidth={1.75} />
                  </span>
                  {etiket}
                </>
              )}
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  );
}

function KullaniciKarti({ kapat }: { kapat?: () => void }) {
  const { kullanici, cikisYap } = useOturum();
  if (!kullanici) return null;

  const cik = async () => {
    kapat?.();
    const onay = await onayIste({
      baslik: 'Çıkış yapılsın mı?',
      mesaj: 'Oturumunuz kapatılacak ve tekrar giriş yapmanız gerekecek.',
      onayMetni: 'Çıkış yap',
    });
    if (onay) await cikisYap();
  };

  return (
    <div className="flex flex-col gap-2 border-t border-[var(--line)] pt-3">
      <div className="flex min-w-0 flex-col px-1">
        <span className="truncate text-sm font-medium text-[var(--ink)]">{kullanici.fullName}</span>
        <span className="truncate text-xs text-[var(--ink-faint)]">
          {kullanici.roleName} · {kullanici.email}
        </span>
      </div>
      <Dugme gorunum="sessiz" ikon={<LogOut size={15} />} onClick={() => void cik()}>
        Çıkış yap
      </Dugme>
    </div>
  );
}

function MarkaBasligi() {
  return (
    <div className="flex min-w-0 flex-col">
      <span className="font-[family-name:var(--font-display)] text-base font-semibold text-[var(--ink)]">
        Deercase Fatura
      </span>
      <span className="text-xs text-[var(--ink-faint)]">e-Arşiv · e-Fatura</span>
    </div>
  );
}

export interface KabukProps {
  baslik: string;
  /** Başlığın altındaki tek satır bağlam. */
  altBaslik?: string;
  /**
   * Nerede olduğunu gösteren yol. Verilmezse yalnızca "Ana ekran" eklenir —
   * her sayfada kırıntı olması, detaydan listeye dönmek için tarayıcı geri
   * düğmesine bağımlı kalmayı önler.
   */
  kirintilar?: KirintiOgesi[];
  /** Sayfa başlığının sağındaki eylemler. */
  eylemler?: ReactNode;
  children: ReactNode;
}

export function Kabuk({ baslik, altBaslik, kirintilar, eylemler, children }: KabukProps) {
  const [menuAcik, setMenuAcik] = useState(false);
  const konum = useLocation();
  const { kullanici } = useOturum();

  /*
   * Kırıntı her sayfada "Ana ekran" ile başlar; ekran yalnızca kendi
   * parçalarını verir. Böylece hiçbir ekran kök bağlantıyı yazmayı unutmaz.
   * Ana ekranın kendisinde kırıntı gösterilmez — kendine yol tarif etmez.
   */
  const anaEkranda = konum.pathname === '/';
  const tumKirintilar: KirintiOgesi[] = anaEkranda
    ? []
    : [{ etiket: 'Ana ekran', yol: '/' }, ...(kirintilar ?? [{ etiket: baslik }])];

  // Sayfa değişince menü kapansın: kullanıcı iki kez dokunmasın.
  useEffect(() => {
    setMenuAcik(false);
  }, [konum.pathname]);

  // Menü açıkken arka plan kaymasın + Esc kapatsın.
  useEffect(() => {
    if (!menuAcik) return;

    const oncekiTasma = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const tusDinleyici = (olay: KeyboardEvent): void => {
      if (olay.key === 'Escape') setMenuAcik(false);
    };
    window.addEventListener('keydown', tusDinleyici);

    return () => {
      document.body.style.overflow = oncekiTasma;
      window.removeEventListener('keydown', tusDinleyici);
    };
  }, [menuAcik]);

  return (
    <div className="flex min-h-dvh flex-col bg-[var(--ground)]">
      <OrtamSeridi />

      <div className="flex flex-1 lg:flex-row">
        {/* ---- Geniş ekran: sabit kenar çubuğu ---- */}
        <aside
          className="hidden shrink-0 flex-col justify-between gap-6 bg-[var(--surface)] p-3 shadow-[var(--shadow-menu)] lg:flex"
          style={{ width: 'var(--kenar-cubugu-genislik)' }}
        >
          <div className="flex flex-col gap-6">
            <div className="px-2 pt-1">
              <MarkaBasligi />
            </div>
            <Gezinme kullanici={kullanici} />
          </div>
          <KullaniciKarti />
        </aside>

        {/* ---- Telefon: kayan menü ---- */}
        {menuAcik && (
          <div className="fixed inset-0 z-50 lg:hidden">
            {/* Arka plan: tıklayınca kapanır. Klavye için de düğme. */}
            <button
              type="button"
              aria-label="Menüyü kapat"
              onClick={() => setMenuAcik(false)}
              className="absolute inset-0 bg-[color-mix(in_srgb,var(--ink)_45%,transparent)]"
            />
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Menü"
              className="absolute inset-y-0 left-0 flex w-[min(20rem,85vw)] flex-col justify-between gap-6 overflow-y-auto border-r border-[var(--line)] bg-[var(--surface)] p-3 shadow-[var(--shadow-float)]"
            >
              <div className="flex flex-col gap-6">
                <div className="flex items-start justify-between gap-2 px-2 pt-1">
                  <MarkaBasligi />
                  <Dugme
                    gorunum="sessiz"
                    boy="sm"
                    aria-label="Menüyü kapat"
                    ikon={<X size={18} />}
                    onClick={() => setMenuAcik(false)}
                  />
                </div>
                <Gezinme kullanici={kullanici} kapat={() => setMenuAcik(false)} />
              </div>
              <KullaniciKarti kapat={() => setMenuAcik(false)} />
            </div>
          </div>
        )}

        <main className="flex min-w-0 flex-1 flex-col">
          {/* Yapışkan başlık: uzun listede kaydırırken hangi ekranda olduğun kaybolmasın. */}
          {/*
            Yapışkan başlık: uzun listede kaydırırken hangi ekranda olduğun
            kaybolmasın. İçerikle AYNI genişlikte ve ortalanmış — sol kenarlar
            hizalı olsun (CLAUDE.md: ekran şablonu sabit).
          */}
          <header className="sticky top-0 z-30 border-b border-[var(--line)] bg-[color-mix(in_srgb,var(--ground)_92%,transparent)] backdrop-blur">
            <div className="mx-auto flex w-full max-w-[90rem] items-start gap-3 px-4 py-3 lg:px-6 lg:py-4">
              <Dugme
                gorunum="sessiz"
                className="lg:hidden"
                aria-label="Menüyü aç"
                aria-expanded={menuAcik}
                ikon={<Menu size={20} />}
                onClick={() => setMenuAcik(true)}
              />
              <div className="min-w-0 flex-1">
                <SayfaBasligi
                  baslik={baslik}
                  altBaslik={altBaslik}
                  kirintilar={tumKirintilar}
                  // Telefonda eylemler alt satıra iner; burada gizlenir.
                  eylemler={
                    eylemler ? <span className="hidden lg:flex lg:gap-2">{eylemler}</span> : undefined
                  }
                />
              </div>
            </div>
            {eylemler && (
              <div className="mx-auto flex w-full max-w-[90rem] flex-wrap items-center gap-2 px-4 pb-3 lg:hidden">
                {eylemler}
              </div>
            )}
          </header>

          {/*
            İçerik genişliği SINIRLI ve ortalanmış.
            Gerekçe: geniş ekranda (1900px+) kart ekranın tamamına yayılınca
            etiket solda, değer ta sağ kenarda kalıyor ve göz ikisini
            birleştiremiyor. Üst sınır tabloların rahat sığacağı kadar geniş,
            metnin okunmaz hâle geleceği kadar değil.

            `.kap` (container-type: inline-size) BURADA olmak zorunda: ekranlar
            `@2xl:grid-cols-2` gibi KAP sorguları kullanıyor ve bu sınıflar bir
            ölçülebilir kap olmadan HİÇ devreye girmez — ızgaralar sessizce tek
            sütun kalır. Ekran değil kap ölçülür (docs/13 bölüm 3).

            Anahtar konum: sayfa değişince kaydırma konumu sıfırlanır.
          */}
          <div
            key={konum.pathname}
            className="kap mx-auto flex w-full max-w-[90rem] flex-1 flex-col gap-6 p-4 lg:p-6"
          >
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
